import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosInstance } from "axios";
import { USER_API } from "config";
import { workspaceKeys } from "queries/workspace.keys";
import { useUserCatalog } from "hooks/useSession";
import {
  WorkspaceFilters,
  WorkspacePayload,
  Workspace,
  WorkspaceCounts,
  WorkspaceMemberPayload,
  WorkspaceMember,
} from "./workspace.types";
import { useMemo, useCallback } from "react";

// Transform workspace member payload to UI format
const transformWorkspaceMember = (member: WorkspaceMemberPayload): WorkspaceMember => {
  // For pending invitations, use invitee_email if email is not available
  const email = member.email || member.invitee_email || "";
  const firstName = member.first_name || "";
  const lastName = member.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  // For pending invitations, use display_name if available, otherwise null (UI will show "—")
  // For active members, use fullName or email as fallback
  const displayName: string | null =
    member.invitation_status === "pending" ? member.display_name || null : fullName || email;

  return {
    id: member.id,
    role: member.role,
    firstName,
    lastName,
    email,
    positionTitle: member.position_title || "",
    companyName: member.company_name || "",
    profileImagePath: member.profile_image_path || undefined,
    isActive: member.is_active,
    verified: member.verified,
    createdAt: member.created_at,
    updatedAt: member.updated_at,
    fullName,
    displayName,
    allProjectAccess: member.all_project_access ?? false,
    projectIds: member.project_ids ?? [],
    invitationStatus: member.invitation_status || undefined,
  };
};

// Transform workspace payload to UI format
const transformWorkspace = (workspace: WorkspacePayload): Workspace => ({
  id: workspace.id,
  name: workspace.name,
  imagePath: workspace.image_path,
  contributeBudgetBenchmarks: workspace.contribute_budget_benchmarks ?? true,
  createdAt: workspace.created_at,
  updatedAt: workspace.updated_at,
  members: workspace.members?.map(transformWorkspaceMember),
  permissions: workspace.permissions
    ? {
        permissions: workspace.permissions.permissions,
        role: workspace.permissions.role,
        workspaceId: workspace.permissions.workspace_id,
        userId: workspace.permissions.user_id,
      }
    : undefined,
});

// Transform workspace array
const transformWorkspaceArray = (workspaces: WorkspacePayload[]): Workspace[] =>
  workspaces.map(transformWorkspace);

export interface UseWorkspaceListResult {
  workspaces: Workspace[];
  rawWorkspaces: WorkspacePayload[];
  filteredWorkspaces: (filter?: (workspace: WorkspacePayload) => boolean) => WorkspacePayload[];
  recommendedWorkspace: WorkspacePayload | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<any>;
  totalCount: number;
  workspaceCounts: WorkspaceCounts;
}

/**
 * Hook for fetching and managing the list of workspaces
 * Based on RBAC v1 backend implementation
 */
export const useWorkspaceList = (
  _filters: WorkspaceFilters,
  _axios: AxiosInstance
): UseWorkspaceListResult => {
  // Get workspaces from the navigation catalog instead of a separate API call.
  const { data: catalog, isLoading, isError, error, refetch } = useUserCatalog();

  // Transform catalog workspaces to the expected format
  const workspacesData = useMemo(() => {
    if (!catalog?.workspaces) return [];

    return catalog.workspaces
      .filter((ws) => ws?.workspace_id)
      .map((ws) => ({
        id: ws.workspace_id,
        name: ws.name,
        image_path: undefined, // Not available in profile data
        created_at: ws.created_at,
        updated_at: ws.updated_at,
        members: undefined, // Not available in profile data
        permissions: undefined, // Not available in profile data
      }));
  }, [catalog?.workspaces]);

  // Get the recommended workspace (last used, latest, or default)
  const recommendedWorkspace = useMemo(() => {
    if (workspacesData.length === 0) return null;

    // Sort by updated_at (most recent first)
    const sortedWorkspaces = [...workspacesData].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );

    // Return the most recently updated workspace as the recommended one
    return sortedWorkspaces[0];
  }, [workspacesData]);

  // Calculate total count
  const totalCount = workspacesData.length;

  // Transform workspaces for UI (memoized)
  const workspacesTransformed = useMemo(
    () => transformWorkspaceArray(workspacesData),
    [workspacesData]
  );

  // Calculate counts
  const workspaceCounts: WorkspaceCounts = useMemo(() => {
    const total = workspacesData.length;
    const active = total; // All workspaces are active
    const pending = 0; // Workspaces don't have pending status
    const inactive = 0; // Workspaces don't have inactive status

    return {
      total,
      active,
      pending,
      inactive,
    };
  }, [workspacesData]);

  // Utility to get filtered workspaces
  const filteredWorkspaces = useCallback(
    (filter?: (workspace: WorkspacePayload) => boolean) => {
      if (!filter) return workspacesData;
      return workspacesData.filter(filter);
    },
    [workspacesData]
  );

  return {
    workspaces: workspacesTransformed,
    rawWorkspaces: workspacesData,
    filteredWorkspaces,
    recommendedWorkspace,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
    totalCount,
    workspaceCounts,
  };
};

/**
 * Factory function to create a hook for fetching a single workspace
 */
export const getUseWorkspace = (axios: AxiosInstance, rawWorkspaces: WorkspacePayload[]) => {
  return (workspaceId?: string): UseQueryResult<Workspace | null, Error> => {
    const normalizedWorkspaceId = workspaceId;

    return useQuery<Workspace | null, Error>({
      queryKey: workspaceKeys.detail(normalizedWorkspaceId || ""),
      queryFn: async () => {
        if (!normalizedWorkspaceId) return null;

        // Check if workspace is already in the list
        const existingWorkspace = rawWorkspaces.find((w) => w.id === normalizedWorkspaceId);
        if (existingWorkspace) {
          return transformWorkspace(existingWorkspace);
        }

        // Fetch from API if not in list
        const response = await axios.get(USER_API.getWorkspace(normalizedWorkspaceId));
        const workspace = response.data.data || response.data;
        return transformWorkspace(workspace);
      },
      enabled: !!normalizedWorkspaceId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      // A workspace from the list is shown on the first render, not one tick later.
      placeholderData: () => {
        const listed = rawWorkspaces.find((w) => w.id === normalizedWorkspaceId);
        return listed ? transformWorkspace(listed) : undefined;
      },
    });
  };
};
